import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash, Trophy } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";
import ShareButtons from "../components/ShareButtons";
import LeaderboardRow from "../components/LeaderboardRow";
import UserBetsModal from "../components/UserBetsModal";

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const { refresh: refreshActiveGroups } = useActiveGroup();
  const [group, setGroup] = useState(null);
  const [openUserId, setOpenUserId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/groups/${groupId}`);
      setGroup(data);
    })();
  }, [groupId]);

  if (!group) return <div className="p-6 text-sm text-neutral-500">{t("loading_dots")}</div>;

  const isOwner = user?.user_id === group.owner_id;
  const canDelete = isOwner && !group.is_demo;

  const handleDelete = async () => {
    setError("");
    setDeleting(true);
    try {
      await api.delete(`/groups/${groupId}`);
      await refreshActiveGroups();
      navigate("/app/groups");
    } catch (e) {
      setError(e?.response?.data?.detail || t("could_not_delete_group"));
      setDeleting(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    const confirmMsg = t("remove_member_confirm").replace("{name}", memberName);
    if (!window.confirm(confirmMsg)) return;

    setError("");
    try {
      await api.delete(`/groups/${groupId}/members/${memberId}`);
      const { data } = await api.get(`/groups/${groupId}`);
      setGroup(data);
      await refreshActiveGroups();
    } catch (e) {
      setError(e?.response?.data?.detail || t("could_not_remove_member"));
    }
  };

  return (
    <div className="px-4 pt-4 pb-10" data-testid="group-detail-page">
      <button
        type="button"
        data-testid="back-to-groups"
        onClick={() => navigate("/app/groups")}
        className="flex items-center gap-1 text-xs font-bold tracking-wider uppercase text-neutral-500 mb-4"
      >
        <ArrowLeft size={14} weight="bold" /> {t("nav_groups")}
      </button>

      <div className="grain relative rounded-2xl bg-[#02371b] text-white p-5 overflow-hidden">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50">
          {group.tournament?.name || t("private_group")}
        </div>
        <h1 className="font-display text-3xl mt-1 leading-none" data-testid="group-name">{group.name}</h1>
        {group.message && <p className="text-sm text-white/70 mt-3 leading-relaxed">{group.message}</p>}
        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/50 mb-2">{t("invite_code")}</div>
          <div className="flex items-center gap-3">
            <div className="font-mono-tab text-3xl font-bold tracking-[0.3em]" data-testid="invite-code-display">{group.invite_code}</div>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white border border-neutral-200 rounded-2xl p-5">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-neutral-500 mb-3">{t("share_success")}</div>
        <ShareButtons code={group.invite_code} groupName={group.name} />
      </div>

      <div className="mt-5">
        <div className="flex items-center gap-2 mb-3">
          <Trophy size={16} weight="fill" className="text-[#41a42b]" />
          <h2 className="font-display text-xl">{t("leaderboard_title")}</h2>
          <span className="text-[10px] font-mono-tab text-neutral-400">· {group.member_count} {t("members")}</span>
        </div>
        <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          {group.leaderboard.length === 0 && (
            <div className="p-6 text-center text-sm text-neutral-500">{t("empty_leaderboard")}</div>
          )}
          {group.leaderboard.map((row) => (
            <div
              key={row.user_id}
              data-testid={`group-leaderboard-open-${row.user_id}`}
              onClick={() => setOpenUserId(row.user_id)}
              className="w-full text-left hover:bg-neutral-50 transition-colors px-1 cursor-pointer"
            >
              <LeaderboardRow
                rank={row.rank}
                name={row.user_id === user?.user_id ? `${row.name} · ${t("you")}` : row.name}
                picture={row.picture}
                points={row.points}
                bets={row.bets}
                isMe={row.user_id === user?.user_id}
                onRemove={
                  isOwner && !group.is_demo && row.user_id !== group.owner_id
                    ? (e) => {
                        e.stopPropagation();
                        handleRemoveMember(row.user_id, row.name);
                      }
                    : null
                }
              />
            </div>
          ))}
        </div>
      </div>

      {canDelete && (
        <div className="mt-6">
          {error && !confirmDelete && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-2xl p-4 text-xs font-semibold mb-4">
              {error}
            </div>
          )}

          {!confirmDelete ? (
            <button
              type="button"
              data-testid="delete-group-btn"
              onClick={() => setConfirmDelete(true)}
              className="w-full flex items-center justify-center gap-2 border border-destructive/40 text-destructive rounded-2xl py-3 text-sm font-bold hover:bg-destructive/5 transition-colors"
            >
              <Trash size={16} weight="bold" /> {t("delete_group")}
            </button>
          ) : (
            <div className="bg-white border border-destructive/30 rounded-2xl p-4" data-testid="delete-group-confirm">
              <p className="text-sm text-[#02371b]">{t("delete_group_confirm")}</p>
              {error && <p className="text-xs text-destructive mt-2">{error}</p>}
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => { setConfirmDelete(false); setError(""); }}
                  disabled={deleting}
                  className="flex-1 border border-neutral-200 rounded-full py-2.5 text-sm font-bold text-neutral-600"
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  data-testid="confirm-delete-group"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 bg-destructive text-destructive-foreground rounded-full py-2.5 text-sm font-bold disabled:opacity-60"
                >
                  {t("delete")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {openUserId && (
        <UserBetsModal userId={openUserId} tournamentId={group.tournament_id} onClose={() => setOpenUserId(null)} />
      )}
    </div>
  );
}
